import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  ListItemButton,
  Tooltip,
  Avatar,
} from '@mui/material';

import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import CategoryIcon from '@mui/icons-material/CategoryOutlined';
import HandshakeIcon from '@mui/icons-material/HandshakeOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCardOutlined';
import InboxIcon from '@mui/icons-material/InboxOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';

const BRAND_BLUE = '#0d4efe';
const BRAND_GREEN = '#01ebb2';
const TEXT_DEFAULT = '#0f172a';      // high-contrast slate
const ICON_DEFAULT = '#475569';      // medium slate
const HEADER_MUTED = '#94a3b8';      // lighter slate for section headers
const DIVIDER_TINT = 'rgba(13, 78, 254, 0.18)';
const ACTIVE_TINT = 'rgba(13, 78, 254, 0.06)';
const HOVER_TINT = 'rgba(15, 23, 42, 0.04)';

const NAVIGATION = [
  { kind: 'header', title: 'Workspace' },
  { segment: 'dashboard', title: 'Dashboard', icon: <DashboardIcon />, exact: true },
  { segment: 'dashboard/service-requests', title: 'Service Requests', icon: <InboxIcon /> },
  { kind: 'header', title: 'Settings' },
  { segment: 'dashboard/service-catalog', title: 'Service Catalog', icon: <CategoryIcon /> },
  { segment: 'dashboard/sms-notifications', title: 'SMS Notifications', icon: <SmsOutlinedIcon /> },
  {
    segment: 'dashboard/partnership-agreement',
    title: 'Partnership Agreement',
    icon: <HandshakeIcon />,
  },
  { kind: 'header', title: 'Billing' },
  { segment: 'payment-methods', title: 'Payment Methods', icon: <CreditCardIcon /> },
  { segment: 'invoices', title: 'Invoices', icon: <ReceiptLongIcon />}
];

function DashboardNavigationBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const navigation = useMemo(
    () =>
      NAVIGATION.filter(
        (item) => !item.adminOnly || user?.Role === 'admin_user',
      ),
    [user?.Role],
  );

  const isActive = (segment, exact = false) => {
    const base = `/${segment}`;
    if (exact) return pathname === base;                       // only /dashboard
    return pathname === base || pathname.startsWith(`${base}/`); // /x and /x/**
  };

  const renderNavigationItem = (item, index, arr) => {
    if (item.kind === 'header') {
      const prev = arr[index - 1];
      const needsLeadingDivider = index > 0 && prev?.kind !== 'divider';

      return (
        <React.Fragment key={index}>
          {needsLeadingDivider && (
            <Divider sx={{ mt: 1.5, mb: 0.5, borderColor: DIVIDER_TINT }} />
          )}
          <ListItem sx={{ pt: index === 0 ? 1 : 2, pb: 0.5, px: 2 }}>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 800,
                letterSpacing: 1.2,
                color: HEADER_MUTED,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: HEADER_MUTED,
                }}
              />
              {item.title}
            </Typography>
          </ListItem>
        </React.Fragment>
      );
    }

    if (item.kind === 'divider') {
      return <Divider key={index} sx={{ my: 1, borderColor: DIVIDER_TINT }} />;
    }

    const active = isActive(item.segment, item.exact);

    return (
      <ListItem key={index} disablePadding sx={{ mb: 0.25 }}>
        <Tooltip title={item.title} placement="right" enterDelay={500}>
          <ListItemButton
            onClick={() => navigate(`/${item.segment}`)}
            aria-current={active ? 'page' : undefined}
            selected={active}
            sx={{
              bgcolor: active ? ACTIVE_TINT : '#fff',
              '&.Mui-selected, &.Mui-selected:hover': { bgcolor: ACTIVE_TINT },

              borderRadius: 1.5,
              pl: 1.5,
              pr: 2,
              position: 'relative',
              color: active ? BRAND_BLUE : TEXT_DEFAULT,
              transition: 'color .15s ease, background-color .15s ease, box-shadow .15s ease',

              // Green left accent bar
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 6,
                bottom: 6,
                width: active ? 4 : 0,
                backgroundColor: BRAND_GREEN,
                borderTopRightRadius: 4,
                borderBottomRightRadius: 4,
                transition: 'width .15s ease',
              },
              '&:hover::before': { width: 4 },

              // Accessible focus ring
              '&:focus-visible': {
                outline: `2px solid ${BRAND_BLUE}`,
                outlineOffset: 2,
              },

              // Hover feedback: background tint (distinct from active tint) + shadow
              '&:hover': {
                bgcolor: active ? ACTIVE_TINT : HOVER_TINT,
                boxShadow: '0 1px 0 rgba(2,6,23,0.06)',
              },

              // Icon coloring
              '& .MuiListItemIcon-root': {
                color: active ? BRAND_BLUE : ICON_DEFAULT,
                minWidth: 36,
                transition: 'color .15s ease',
              },

              // Typography weight for active item
              '& .MuiListItemText-primary': {
                fontWeight: active ? 700 : 500,
              },
            }}
          >
            {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
            <ListItemText primary={item.title} primaryTypographyProps={{ noWrap: true }} />
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  const accountLabel =
    [user?.FirstName, user?.LastName].filter(Boolean).join(' ') ||
    user?.Email ||
    'NKY Connect';
  const accountInitial = (user?.FirstName || user?.Email || 'N').charAt(0).toUpperCase();

  return (
    <Box sx={{ width: '100%', bgcolor: '#fff' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5 }}>
        <Avatar sx={{ width: 28, height: 28, fontSize: 14, bgcolor: BRAND_BLUE }}>
          {accountInitial}
        </Avatar>
        <Typography
          variant="subtitle2"
          noWrap
          sx={{ fontWeight: 700, color: TEXT_DEFAULT }}
        >
          {accountLabel}
        </Typography>
      </Box>
      <Divider sx={{ borderColor: DIVIDER_TINT }} />
      <List>{navigation.map(renderNavigationItem)}</List>
      <Divider sx={{ mt: 1.5, borderColor: DIVIDER_TINT }} />
    </Box>
  );
}

export default DashboardNavigationBar;